// Gut / Digestion type quiz — ORIGINAL wording, non-diagnostic. 15 questions across
// 4 dimensions. Tally scoring → 4 digestion "types", each routed to digestive herbs.
const gut = {
  slug: "gut",
  title: "What's your digestion type?",
  emoji: "🌱",
  blurb: "A 15-question check across 4 dimensions to find your digestion pattern and the herbs to soothe it.",
  seoTitle: "Gut Health Quiz: What's Your Digestion Type? | Sarv",
  seoDescription:
    "Take the free gut health quiz to find your digestion type — sluggish, bloated, acidic, or a nervous stomach — and the natural remedies traditionally used for each.",
  keywords:
    "gut health quiz, digestion quiz, bloating remedies, natural remedies for digestion, herbs for bloating, why am I bloated",
  intro: "15 questions across symptoms, patterns, triggers, and the gut–stress link. General wellness only, not a medical diagnosis.",
  evidenceNote:
    "These questions are organized around how digestion is understood today — symptom pattern, bowel habits, food triggers, and the gut–brain axis (why stress hits the stomach). The herb suggestions reflect traditional carminative and soothing use; peppermint and ginger in particular have systematic-review evidence for digestive symptoms. This is an educational tool, not a diagnosis.",
  references: [
    {
      label: "Irritable Bowel Syndrome and Complementary Health Approaches: What the Science Says",
      publisher: "NCCIH / NIH",
      url: "https://www.nccih.nih.gov/health/providers/digest/irritable-bowel-syndrome-and-complementary-health-approaches-science",
    },
    {
      label: "Peppermint oil for the treatment of IBS: a systematic review and meta-analysis",
      publisher: "NCBI / NIH",
      url: "https://www.ncbi.nlm.nih.gov/books/NBK169116/",
    },
    {
      label: "Soluble fibre, antispasmodics, peppermint oil & gut-brain neuromodulators in IBS: network meta-analysis",
      publisher: "PubMed / NIH",
      url: "https://pubmed.ncbi.nlm.nih.gov/31859183/",
    },
    {
      label: "Stress and the brain–gut axis in functional and chronic-inflammatory GI diseases",
      publisher: "PubMed / NIH",
      url: "https://pubmed.ncbi.nlm.nih.gov/31715444/",
    },
    {
      label: "Ginger for nausea and vomiting: an overview of systematic reviews and meta-analyses",
      publisher: "PubMed / NIH",
      url: "https://pubmed.ncbi.nlm.nih.gov/38072785/",
    },
    {
      label: "Ginger in gastrointestinal disorders: a systematic review of clinical trials",
      publisher: "PMC / NIH",
      url: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6341159/",
    },
  ],
  scoring: { mode: "tally" },
  questions: [
    // --- Symptoms ---
    {
      section: "Symptoms",
      question: "After meals, what do you notice most?",
      options: [
        { label: "Heaviness, or feeling backed up", scores: { sluggish: 1 } },
        { label: "Bloating, gas, or a tight belly", scores: { bloated: 1 } },
        { label: "Burning, reflux, or acidity", scores: { acid: 1 } },
        { label: "Cramping or urgency, especially when stressed", scores: { nervous: 1 } },
      ],
    },
    {
      section: "Symptoms",
      question: "Which discomfort is most familiar?",
      options: [
        { label: "Sluggish, slow digestion", scores: { sluggish: 1 } },
        { label: "Bloating and distension", scores: { bloated: 1 } },
        { label: "Acid or a sour stomach", scores: { acid: 1 } },
        { label: "A 'nervous stomach'", scores: { nervous: 1 } },
      ],
    },
    {
      section: "Symptoms",
      question: "Which sensation shows up most?",
      options: [
        { label: "Heaviness and fullness that lingers", scores: { sluggish: 1 } },
        { label: "Pressure, gas, and distension", scores: { bloated: 1 } },
        { label: "Burning or sourness rising up", scores: { acid: 1 } },
        { label: "Cramping, gurgling, or butterflies", scores: { nervous: 1 } },
      ],
    },
    {
      section: "Symptoms",
      question: "How would a friend describe your stomach?",
      options: [
        { label: "Slow and sluggish", scores: { sluggish: 1 } },
        { label: "Gassy and bloated", scores: { bloated: 1 } },
        { label: "Acidic and heartburn-prone", scores: { acid: 1 } },
        { label: "Sensitive and stress-reactive", scores: { nervous: 1 } },
      ],
    },
    // --- Patterns ---
    {
      section: "Patterns",
      question: "How are your bathroom habits?",
      options: [
        { label: "Infrequent or hard to go", scores: { sluggish: 1 } },
        { label: "Unpredictable, gassy, and uncomfortable", scores: { bloated: 1 } },
        { label: "Regular, but I get heartburn", scores: { acid: 1 } },
        { label: "They swing with my mood and stress", scores: { nervous: 1 } },
      ],
    },
    {
      section: "Patterns",
      question: "What are your bowel movements usually like?",
      options: [
        { label: "Hard, infrequent, or a feeling of incomplete emptying", scores: { sluggish: 1 } },
        { label: "Lots of wind and gas", scores: { bloated: 1 } },
        { label: "Fairly normal, but with heartburn separately", scores: { acid: 1 } },
        { label: "They change with stress — loose, then constipated", scores: { nervous: 1 } },
      ],
    },
    {
      section: "Patterns",
      question: "How regular is your digestion overall?",
      options: [
        { label: "Slow and infrequent", scores: { sluggish: 1 } },
        { label: "Unpredictable, with lots of wind", scores: { bloated: 1 } },
        { label: "Regular, but with reflux", scores: { acid: 1 } },
        { label: "It shifts with my stress levels", scores: { nervous: 1 } },
      ],
    },
    {
      section: "Patterns",
      question: "When do you feel best, digestively?",
      options: [
        { label: "After I've finally 'gone'", scores: { sluggish: 1 } },
        { label: "When I avoid gas-forming foods", scores: { bloated: 1 } },
        { label: "When I skip spicy/acidic foods and don't eat late", scores: { acid: 1 } },
        { label: "On calm, low-stress days", scores: { nervous: 1 } },
      ],
    },
    // --- Triggers ---
    {
      section: "Triggers",
      question: "When does it tend to get worse?",
      options: [
        { label: "After heavy or low-fiber meals", scores: { sluggish: 1 } },
        { label: "After certain foods (dairy, beans, etc.)", scores: { bloated: 1 } },
        { label: "With spicy, fatty, or late meals", scores: { acid: 1 } },
        { label: "When I'm anxious or under pressure", scores: { nervous: 1 } },
      ],
    },
    {
      section: "Triggers",
      question: "Which foods or habits set you off most?",
      options: [
        { label: "Low fiber, too little water, sitting all day", scores: { sluggish: 1 } },
        { label: "Dairy, beans, onions, or carbonation", scores: { bloated: 1 } },
        { label: "Coffee, citrus, tomato, fried or late meals", scores: { acid: 1 } },
        { label: "Eating on the run or while anxious", scores: { nervous: 1 } },
      ],
    },
    {
      section: "Triggers",
      question: "What time of day is worst for you?",
      options: [
        { label: "Mornings, until I've 'gone'", scores: { sluggish: 1 } },
        { label: "Afternoon or evening as gas builds", scores: { bloated: 1 } },
        { label: "After meals and when lying down", scores: { acid: 1 } },
        { label: "Any time I'm stressed", scores: { nervous: 1 } },
      ],
    },
    {
      section: "Triggers",
      question: "How do rich, heavy, or unusual meals affect you?",
      options: [
        { label: "They sit like a brick", scores: { sluggish: 1 } },
        { label: "They leave me bloated and gassy", scores: { bloated: 1 } },
        { label: "They trigger heartburn", scores: { acid: 1 } },
        { label: "They're fine unless I'm anxious", scores: { nervous: 1 } },
      ],
    },
    // --- Stress & goals ---
    {
      section: "Stress & goals",
      question: "How does stress affect your gut?",
      options: [
        { label: "It slows everything down", scores: { sluggish: 1 } },
        { label: "It leaves me gassy and bloated", scores: { bloated: 1 } },
        { label: "It brings on acid or a knot in my stomach", scores: { acid: 1 } },
        { label: "It's the main trigger — cramps, urgency, butterflies", scores: { nervous: 1 } },
      ],
    },
    {
      section: "Stress & goals",
      question: "Which best describes your gut's temperament?",
      options: [
        { label: "Slow to respond", scores: { sluggish: 1 } },
        { label: "Easily gassy and reactive", scores: { bloated: 1 } },
        { label: "Prone to acid and heat", scores: { acid: 1 } },
        { label: "Highly sensitive to emotions", scores: { nervous: 1 } },
      ],
    },
    {
      section: "Stress & goals",
      question: "What's your main goal?",
      options: [
        { label: "Get things moving regularly", scores: { sluggish: 1 } },
        { label: "Beat the bloat", scores: { bloated: 1 } },
        { label: "Cool down acid and reflux", scores: { acid: 1 } },
        { label: "Calm a stress-sensitive gut", scores: { nervous: 1 } },
      ],
    },
  ],
  results: {
    sluggish: {
      name: "The Sluggish Gut",
      emoji: "🐌",
      tagline: "Digestion that runs slow and heavy.",
      summary:
        "Your answers describe a digestive system that runs slow — meals sit heavy, and things don't move as regularly as they should. Sluggish digestion is often a matter of too little of what keeps the gut in motion: fiber, fluids, and physical movement, sometimes combined with large, infrequent meals that overwhelm an already-slow system. The traditional approach gently stimulates rather than forces — bitter and warming herbs used to 'wake up' digestion, alongside more fiber, better hydration, daily movement, and regular meal timing. Consistency matters far more than intensity here: steady daily habits tend to restore a natural rhythm better than occasional strong remedies, which can leave the gut more sluggish once they wear off.",
      focus: [
        "More fiber, water, and daily movement to keep things moving.",
        "Bitter and warming herbs traditionally used to stimulate digestion.",
        "Regular meal timing rather than large, infrequent meals.",
      ],
      herbs: ["Ginger", "Dandelion root", "Triphala"],
      categorySlug: "digestive-health",
    },
    bloated: {
      name: "The Bloated Gut",
      emoji: "🎈",
      tagline: "Gas, bloating, and a belly that feels tight.",
      summary:
        "Your answers point to gas and bloating as your main pattern — pressure and distension that often flare after particular foods. This usually comes down to how certain foods ferment in the gut and how much air gets swallowed while eating, rather than anything being fundamentally 'wrong' with your stomach. The most useful approach is part detective work, part soothing: tracking which foods reliably set you off (common culprits include dairy, beans, onions, and carbonation) while using carminative herbs — the traditional class used specifically to ease gas and relax the gut wall. Eating slowly and favoring warm, well-cooked meals over cold or raw ones during a flare tends to help noticeably, since both reduce the fermentation and swallowed air that drive the bloat.",
      focus: [
        "Track trigger foods and eat slowly to reduce swallowed air.",
        "Carminative herbs traditionally used to relieve gas and bloating.",
        "Warm, well-cooked meals over cold or raw when you're flaring.",
      ],
      herbs: ["Fennel", "Peppermint", "Ginger"],
      categorySlug: "digestive-health",
    },
    acid: {
      name: "The Acid Gut",
      emoji: "🔥",
      tagline: "Reflux, heartburn, and a sour stomach.",
      summary:
        "Your answers describe a tendency toward acidity and reflux — burning, heartburn, or a sour stomach, often triggered by spicy, fatty, or late meals. This is frequently as much about timing and pressure as it is about acid itself: large or late meals, lying down too soon after eating, and stress all make reflux more likely. The traditional approach combines soothing, coating (demulcent) herbs that form a protective layer over irritated tissue with a few practical habits — smaller and earlier meals, staying upright afterward, and easing back on the usual triggers. Because stress reliably worsens reflux, calming the nervous system is an underrated part of the picture. If heartburn is frequent or severe, it's worth checking in with a provider, since persistent reflux needs proper evaluation.",
      focus: [
        "Smaller, earlier meals; go easy on spicy, fatty, and acidic foods.",
        "Soothing, coating (demulcent) herbs traditionally used for acidity.",
        "Staying upright after eating and managing stress, which worsens reflux.",
      ],
      herbs: ["Slippery elm", "Marshmallow root", "Chamomile"],
      categorySlug: "digestive-health",
    },
    nervous: {
      name: "The Nervous Gut",
      emoji: "🦋",
      tagline: "A stomach that reacts to stress and mood.",
      summary:
        "Your answers point to a gut that's tightly wired to your emotions — the classic 'nervous stomach,' where stress and anxiety show up as cramps, urgency, or butterflies. This reflects the gut–brain axis, the constant two-way communication between your nervous system and digestive tract, which is why your stomach so often reacts to pressure before your mind has caught up. The key insight is that treating digestion alone usually isn't enough here: the most effective approach pairs calming, nervine herbs with genuine stress support and mindful, unhurried meals away from screens and pressure. In other words, settle the nervous system and the gut tends to follow — which is why stress management is as much a 'digestive' remedy as anything you'd take for the stomach itself.",
      focus: [
        "Address stress directly — the gut and nervous system are linked.",
        "Calming herbs traditionally used for a stress-sensitive stomach.",
        "Mindful, unhurried meals away from screens and pressure.",
      ],
      herbs: ["Chamomile", "Peppermint", "Lemon balm"],
      categorySlug: "digestive-health",
    },
  },
};

export default gut;
